import { Box, BoxProps, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type SectionVariant = 'default' | 'subtle' | 'gradient' | 'glass';

export interface SectionProps extends BoxProps {
  variant?: SectionVariant;
  gradientVariant?: GradientVariant;
  gradientOpacity?: number;
  fullWidth?: boolean;
  fullHeight?: boolean;
  children?: React.ReactNode;
}

const Section = forwardRef<HTMLDivElement, SectionProps>(
  (
    {
      variant = 'default',
      gradientVariant = 'primary',
      gradientOpacity = 0.1,
      fullWidth = false,
      fullHeight = false,
      style,
      children,
      ...rest
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const gradient = theme.other.gradients[gradientVariant];

    const getBackgroundStyle = (): React.CSSProperties => {
      switch (variant) {
        case 'subtle':
          return {
            backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.gray[0],
          };
        case 'gradient': {
          const opacity = Math.round(gradientOpacity * 255).toString(16).padStart(2, '0');
          return {
            backgroundImage: `linear-gradient(135deg, ${gradient.from}${opacity}, ${gradient.to}${opacity})`,
          };
        }
        case 'glass':
          return {
            backdropFilter: 'blur(10px)',
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)',
          };
        default:
          return {};
      }
    };

    return (
      <Box
        ref={ref}
        style={{
          width: fullWidth ? '100%' : undefined,
          height: fullHeight ? '100%' : undefined,
          minHeight: fullHeight ? '100vh' : undefined,
          ...getBackgroundStyle(),
          ...style,
        }}
        {...rest}
      >
        {children}
      </Box>
    );
  }
);

Section.displayName = 'Section';

export default Section;
